import { apiPath } from "../lib/apiPath";

export type AchievementRow = {
  id: string;
  title: string;
  description: string;
  criteriaSummary: string;
  showHowToComplete: boolean;
  done: boolean;
  unlockedAt: string | null;
};

export type AdminAchievementRow = {
  id: string;
  title: string;
  description: string;
  criteria: AchievementCriteriaPayload | null;
  criteriaSummary: string;
  showHowToComplete: boolean;
  dotsReward: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type AchievementCriteriaPayload =
  | { kind: "modeScore"; mode: string; minScore: number }
  | { kind: "progressionLevel"; minLevel: number }
  | { kind: "speedrunTime"; level: number; maxTimeMs: number }
  | { kind: "friends"; minCount: number }
  | { kind: "playtime"; minSec: number }
  | { kind: "deaths"; minCount: number }
  | { kind: "dotsBalance"; minDots: number };

export async function fetchAchievements(): Promise<AchievementRow[]> {
  const r = await fetch(apiPath("/api/achievements"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`achievements ${r.status}`);
  const data = (await r.json()) as { items: AchievementRow[] };
  return data.items ?? [];
}

export async function fetchAdminAchievements(): Promise<AdminAchievementRow[]> {
  const r = await fetch(apiPath("/api/admin-achievements"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin achievements ${r.status}`);
  const data = (await r.json()) as { items: AdminAchievementRow[] };
  return data.items ?? [];
}

export async function createAdminAchievement(payload: {
  title: string;
  description: string;
  criteria: AchievementCriteriaPayload;
  showHowToComplete?: boolean;
  dotsReward?: number;
  sortOrder?: number;
}): Promise<AdminAchievementRow> {
  const r = await fetch(apiPath("/api/admin-achievements"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`create achievement ${r.status}`);
  const data = (await r.json()) as { item: AdminAchievementRow };
  return data.item;
}

export async function updateAdminAchievement(payload: {
  id: string;
  title?: string;
  description?: string;
  criteria?: AchievementCriteriaPayload;
  showHowToComplete?: boolean;
  dotsReward?: number;
  sortOrder?: number;
}): Promise<AdminAchievementRow> {
  const r = await fetch(apiPath("/api/admin-achievements"), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`update achievement ${r.status}`);
  const data = (await r.json()) as { item: AdminAchievementRow };
  return data.item;
}

export async function deleteAdminAchievement(id: string): Promise<void> {
  const p = new URLSearchParams({ id });
  const r = await fetch(`${apiPath("/api/admin-achievements")}?${p}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete achievement ${r.status}`);
}

export function countCompletedAchievements(rows: AchievementRow[]): number {
  return rows.filter((r) => r.done).length;
}
